import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  const [counters, setCounters] = useState({
    totalSupply: 0,
    circulating: 0,
    tokenHolders: 0,
    transactions: 0
  });

  // Smooth scroll effect for parallax - DEFERRED to reduce LCP delay
  useEffect(() => {
    const initParallax = () => {
      const handleScroll = () => {
        requestAnimationFrame(() => setScrollY(window.scrollY));
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    };

    // Defer parallax initialization until after initial render
    const timer = setTimeout(initParallax, 100);
    return () => clearTimeout(timer);
  }, []);

  // Animated counters - DEFERRED using requestIdleCallback to reduce LCP impact
  useEffect(() => {
    const startCounters = () => {
      const animateCounter = (start: number, end: number, duration: number, callback: (value: number) => void) => {
        let startTime: number | null = null;
        const step = (timestamp: number) => {
          if (!startTime) startTime = timestamp;
          const progress = Math.min((timestamp - startTime) / duration, 1);
          const current = Math.floor(progress * (end - start) + start);
          callback(current);
          if (progress < 1) {
            window.requestAnimationFrame(step);
          }
        };
        window.requestAnimationFrame(step);
      };

      animateCounter(0, 100, 2000, (value) => setCounters(prev => ({ ...prev, totalSupply: value })));
      animateCounter(0, 10, 2000, (value) => setCounters(prev => ({ ...prev, circulating: value })));
      animateCounter(0, 5000, 1500, (value) => setCounters(prev => ({ ...prev, tokenHolders: value })));
      animateCounter(0, 10, 2500, (value) => setCounters(prev => ({ ...prev, transactions: value })));
    };

    // Use requestIdleCallback for better scheduling, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      const idleCallbackId = (window as any).requestIdleCallback(() => startCounters());
      return () => (window as any).cancelIdleCallback(idleCallbackId);
    } else {
      const timer = setTimeout(startCounters, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          {/* Subtle grid overlay with parallax */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='80' height='80' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 80 0 L 0 0 0 80' fill='none' stroke='rgba(245,158,11,0.06)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
              transform: `translateY(${scrollY * 0.4}px)`,
              opacity: 0.6,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">


          {/* Hero Title — Liquid Gold gradient, no white override */}
          <h1
            className="hero-title font-black mb-8"
            style={{ fontSize: 'clamp(2.4rem, 6vw, 5rem)' }}
          >
            {t('hero.title1')}{' '}{t('hero.title2')}
          </h1>

          {/* Hero Subtitle — clean, no box */}
          <p className="hero-subtitle mb-12">
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons — Gold-only, no red hover */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-14">
            <Link to="/buy">
              <button className="btn btn-primary" style={{ fontSize: '1rem', padding: '0.95rem 2rem' }}>
                <i className="fas fa-shopping-cart" style={{ marginRight: '0.6rem' }} />
                {t('hero.buyButton')}
              </button>
            </Link>
            <Link to="/whitepaper">
              <button className="btn btn-secondary" style={{ fontSize: '1rem', padding: '0.95rem 2rem' }}>
                <i className="fas fa-book-open" style={{ marginRight: '0.6rem' }} />
                {t('hero.whitepaperButton')}
              </button>
            </Link>
          </div>

          {/* Proof Points — warm gold checkmarks */}
          <div
            className="flex flex-wrap gap-6 md:gap-10 justify-center"
            style={{ color: 'var(--text-secondary)' }}
          >
            {[t('hero.proof1'), t('hero.proof2'), t('hero.proof3')].map((proof, i) => (
              <span key={i} className="flex items-center gap-2.5 text-sm font-medium">
                <svg
                  width="18" height="18" viewBox="0 0 18 18" fill="none"
                  style={{ flexShrink: 0 }}
                >
                  <circle cx="9" cy="9" r="9" fill="rgba(245,158,11,0.15)" />
                  <path
                    d="M5 9l3 3 5-5"
                    stroke="#F59E0B"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {proof}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* NFT & Token Metrics Section */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('metrics.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6" style={{ color: 'var(--text-primary)' }}>{t('metrics.title')}</h2>
            <p className="max-w-3xl mx-auto leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {t('metrics.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="metric-counter">
              <div className="text-5xl md:text-6xl font-black text-gold mb-3 tabular-nums">
                {counters.totalSupply.toLocaleString()}M
              </div>
              <div className="text-gray-400 font-medium">{t('metrics.totalSupply')}</div>
            </div>

            <div className="metric-counter">
              <div className="text-5xl md:text-6xl font-black text-gold mb-3 tabular-nums">
                {counters.circulating.toLocaleString()}M
              </div>
              <div className="text-gray-400 font-medium">{t('metrics.circulating')}</div>
            </div>

            <div className="metric-counter">
              <div className="text-5xl md:text-6xl font-black text-gold mb-3 tabular-nums">
                {counters.tokenHolders.toLocaleString()}+
              </div>
              <div className="text-gray-400 font-medium">{t('metrics.tokenHolders')}</div>
            </div>

            <div className="metric-counter">
              <div className="text-5xl md:text-6xl font-black text-gold mb-3 tabular-nums">
                {counters.transactions.toLocaleString()}K+
              </div>
              <div className="text-gray-400 font-medium">{t('metrics.transactions')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose IDNGold NFTs Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('whyChoose.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6" style={{ color: 'var(--text-primary)' }}>{t('whyChoose.title')}</h2>
            <p className="max-w-3xl mx-auto leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {t('whyChoose.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'fa-exchange-alt', titleKey: 'whyChoose.features.liquidity.title', descKey: 'whyChoose.features.liquidity.description' },
              { icon: 'fa-th', titleKey: 'whyChoose.features.multipleUtility.title', descKey: 'whyChoose.features.multipleUtility.description' },
              { icon: 'fa-network-wired', titleKey: 'whyChoose.features.suiNetwork.title', descKey: 'whyChoose.features.suiNetwork.description' },
              { icon: 'fa-coins', titleKey: 'whyChoose.features.goldConvert.title', descKey: 'whyChoose.features.goldConvert.description' },
            ].map(({ icon, titleKey, descKey }, i) => (
              <div className="modern-card group" key={i}>
                <div className="feature-icon">
                  <i className={`fas ${icon} text-2xl`} style={{ color: 'var(--gold)' }} />
                </div>
                <h3
                  className="text-xl font-bold mb-3 transition-colors"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {t(titleKey)}
                </h3>
                <p className="leading-relaxed text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {t(descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Why IDNGold Section */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="glass-card">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-6">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('mission.tag1')}</span>
              </div>
              <h3 className="text-3xl font-black text-white mb-6">{t('mission.title1')}</h3>
              <p className="text-gray-400 leading-relaxed text-lg">
                {t('mission.description1')}
              </p>
            </div>

            <div className="glass-card">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-6">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('mission.tag2')}</span>
              </div>
              <h3 className="text-3xl font-black text-white mb-6">{t('mission.title2')}</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-4 text-gray-400">
                  <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-gold text-xs"></i>
                  </div>
                  <span className="text-lg">{t('mission.points.accessible')}</span>
                </li>
                <li className="flex items-start gap-4 text-gray-400">
                  <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-gold text-xs"></i>
                  </div>
                  <span className="text-lg">{t('mission.points.liquid')}</span>
                </li>
                <li className="flex items-start gap-4 text-gray-400">
                  <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-gold text-xs"></i>
                  </div>
                  <span className="text-lg">{t('mission.points.transparent')}</span>
                </li>
                <li className="flex items-start gap-4 text-gray-400">
                  <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="fas fa-check text-gold text-xs"></i>
                  </div>
                  <span className="text-lg">{t('mission.points.connected')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('howItWorks.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6" style={{ color: 'var(--text-primary)' }}>{t('howItWorks.title')}</h2>
            <p className="max-w-3xl mx-auto leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {t('howItWorks.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((step) => (
              <div className="step-card modern-card text-center" key={step}>
                <div className="step-number">{step}</div>
                <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  {t(`howItWorks.steps.step${step}.title`)}
                </h3>
                <p className="leading-relaxed text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {t(`howItWorks.steps.step${step}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('useCases.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('useCases.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('useCases.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="glass-card text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="fas fa-chart-line text-4xl text-gold"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('useCases.savings.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('useCases.savings.description')}
              </p>
            </div>


            <div className="glass-card text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="fas fa-chart-line text-4xl text-gold"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('useCases.trading.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('useCases.trading.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Learn/Resources Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('learn.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('learn.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('learn.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="modern-card group">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-4">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('learn.whitepaper.tag')}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('learn.whitepaper.title')}</h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('learn.whitepaper.description')}
              </p>
              <Link to="/whitepaper" className="inline-flex items-center gap-3 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2">
                {t('learn.whitepaper.action')}
                <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>

            <div className="modern-card group">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-4">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('learn.howToBuy.tag')}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('learn.howToBuy.title')}</h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('learn.howToBuy.description')}
              </p>
              <Link to="/buy" className="inline-flex items-center gap-3 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2">
                {t('learn.howToBuy.action')}
                <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>

            <div className="modern-card group">
              <div className="inline-block px-3 py-1 bg-gold/10 rounded-full mb-4">
                <span className="text-gold text-xs font-semibold uppercase tracking-wider">{t('learn.utilities.tag')}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{t('learn.utilities.title')}</h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('learn.utilities.description')}
              </p>
              <Link to="/utilities" className="inline-flex items-center gap-3 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2">
                {t('learn.utilities.action')}
                <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Partners Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('partners.eyebrow')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
              {t('partners.title')}
            </h2>
            <p className="text-base max-w-3xl mx-auto leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {t('partners.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="partner-card group">
              <div className="relative mb-8">
                <div className="absolute inset-0 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))' }}></div>
                <img src="/IndotanGroup.webp" alt="Indotan Group" className="relative h-24 mx-auto object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500" />
              </div>
              <h3 className="text-2xl font-bold mb-4 transition-colors" style={{ color: 'var(--text-primary)' }}>{t('partners.indotan.name')}</h3>
              <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {t('partners.indotan.description')}
              </p>
            </div>

            <div className="partner-card group">
              <div className="relative mb-8">
                <div className="absolute inset-0 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))' }}></div>
                <img src="/PTNHM_FC.webp" alt="PT Nusa Halmahera Minerals" className="relative h-24 mx-auto object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500" />
              </div>
              <h3 className="text-2xl font-bold mb-4 transition-colors" style={{ color: 'var(--text-primary)' }}>{t('partners.nhm.name')}</h3>
              <p className="leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                {t('partners.nhm.description')}
              </p>
              <a
                href="https://www.nhm.co.id/en/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2"
              >
                {t('partners.nhm.link')}
                <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Supported By Section with Marquee */}
      <section className="section relative bg-bg-secondary/30 overflow-hidden">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-4">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('trustedPartners.eyebrow')}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white/80 mb-3">
              {t('trustedPartners.title')}
            </h2>
            <p className="text-gray-400 text-sm max-w-3xl mx-auto">
              {t('trustedPartners.description')}
            </p>
          </div>

          {/* Marquee Container */}
          <div className="relative">
            {/* Gradient Overlays */}
            <div className="absolute left-0 top-0 bottom-0 w-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, #080705, rgba(8,7,5,0.5), transparent)' }}></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, #080705, rgba(8,7,5,0.5), transparent)' }}></div>

            {/* Marquee Track */}
            <div className="flex overflow-hidden">
              <div className="flex animate-marquee gap-16 items-center py-8">
                {/* First Set of Logos */}
                <div className="flex items-center justify-center min-w-[220px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/logo-komdigi.png"
                    alt="Kementerian Komunikasi dan Digital"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[220px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/logo-pse-small.png"
                    alt="PSE Kominfo"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[280px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/IndotanGroup.webp"
                    alt="Indotan Group"
                    className="max-h-20 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/PTNHM_FC.webp"
                    alt="PT Nusa Halmahera Minerals"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>


                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/xendit.png"
                    alt="Xendit"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/Sui_Symbol_Sea.png"
                    alt="Sui Network"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/cetus.png"
                    alt="Cetus Protocol"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <a
                  href="https://skynet.certik.com/projects/idngold"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                >
                  <img
                    src="/CertiK.png"
                    alt="CertiK"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </a>

                <a
                  href="https://coinmarketcap.com/currencies/idngold/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                >
                  <img
                    src="/cmc.png"
                    alt="CoinMarketCap"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </a>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/peruri.webp"
                    alt="Peruri"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                {/* Duplicate Set for Seamless Loop */}
                <div className="flex items-center justify-center min-w-[220px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/logo-komdigi.png"
                    alt="Kementerian Komunikasi dan Digital"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[220px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/logo-pse-small.png"
                    alt="PSE Kominfo"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[280px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/IndotanGroup.webp"
                    alt="Indotan Group"
                    className="max-h-20 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/PTNHM_FC.webp"
                    alt="PT Nusa Halmahera Minerals"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>


                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/xendit.png"
                    alt="Xendit"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/Sui_Symbol_Sea.png"
                    alt="Sui Network"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/cetus.png"
                    alt="Cetus Protocol"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>

                <a
                  href="https://skynet.certik.com/projects/idngold"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                >
                  <img
                    src="/CertiK.png"
                    alt="CertiK"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </a>

                <a
                  href="https://coinmarketcap.com/currencies/idngold/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105"
                >
                  <img
                    src="/cmc.png"
                    alt="CoinMarketCap"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </a>

                <div className="flex items-center justify-center min-w-[250px] h-28 px-8 bg-white/5 backdrop-blur-sm rounded-xl border border-gold/10 hover:border-gold/30 hover:bg-white/10 transition-all duration-300 hover:scale-105">
                  <img
                    src="/peruri.webp"
                    alt="Peruri"
                    className="max-h-16 max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section pb-20">
        <div className="container">
          <div className="cta-section relative">
            <h2
              className="text-4xl md:text-5xl font-black mb-6 relative z-10"
              style={{ color: 'var(--text-primary)' }}
            >
              {t('finalCta.title')}
            </h2>
            <p
              className="mb-12 max-w-2xl mx-auto leading-relaxed relative z-10"
              style={{ color: 'var(--text-secondary)', fontSize: '1.05rem' }}
            >
              {t('finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center relative z-10">
              <Link to="/buy">
                <button className="btn btn-primary" style={{ fontSize: '1rem', padding: '0.95rem 2.25rem' }}>
                  {t('finalCta.buyButton')}
                </button>
              </Link>
              <Link to="/about">
                <button className="btn btn-secondary" style={{ fontSize: '1rem', padding: '0.95rem 2.25rem' }}>
                  {t('finalCta.learnButton')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};